import { useEffect } from 'react';
import { X } from 'lucide-react';
import {
    TERMS_TITLE,
    TERMS_VERSION,
    TERMS_SECTIONS,
} from '@/shared/constants/terms';

interface Props {
    open:    boolean;
    onClose: () => void;
}

/**
 * View-only Terms & Conditions modal. No download, no print, just a close
 * affordance — matches the legacy UI's `TermsModal.jsx`.
 */
export function TermsModal({ open, onClose }: Props) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[1100] flex items-center justify-center bg-ink-900/60 p-4"
        >
            <div
                className="flex max-h-[88vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-card"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3 border-b border-ink-100 px-6 py-4">
                    <div>
                        <div className="font-ui text-[11px] uppercase tracking-wider text-ink-500">
                            Legal · {TERMS_VERSION}
                        </div>
                        <h2 className="font-display text-lg font-bold leading-snug text-brand-navy">
                            {TERMS_TITLE}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close terms"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-ink-50"
                    >
                        <X className="h-5 w-5 text-ink-500" />
                    </button>
                </div>

                <div className="space-y-5 overflow-y-auto px-6 py-4 text-sm leading-relaxed text-ink-700">
                    {TERMS_SECTIONS.map((sec) => (
                        <section key={sec.n}>
                            <h3 className="mb-1.5 font-display font-semibold text-brand-navy">
                                {sec.n}. {sec.h}
                            </h3>
                            {Array.isArray(sec.body) ? (
                                sec.body.map((p, i) => (
                                    <p key={i} className="mb-2">
                                        {p}
                                    </p>
                                ))
                            ) : (
                                <p>{sec.body}</p>
                            )}
                        </section>
                    ))}
                </div>

                <div className="flex items-center justify-end border-t border-ink-100 px-6 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
